import React, { useState } from 'react';
import { Lock, Download, Trash2, AlertTriangle, ShieldCheck, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from './ui/dialog';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Privacy & GDPR section for the Settings page.
 *
 * Implements:
 *   - Article 13: clear notice of what we collect & why (lawful basis)
 *   - Article 15: Right of access (data export)
 *   - Article 17: Right to erasure (account deletion)
 */
export const PrivacyGdprSection = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await api.exportMyData();
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `assagart-data-export-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Data export downloaded.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (confirmText !== 'DELETE MY ACCOUNT') return;
    setDeleting(true);
    try {
      await api.deleteMyAccount();
      toast.success('Your account has been deleted.');
      logout?.();
      setTimeout(() => navigate('/'), 600);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not delete account.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="luxury-card p-8 mb-6" data-testid="privacy-gdpr-section">
      <div className="flex items-center gap-3 mb-6">
        <Lock size={20} className="text-gold" />
        <h2 className="font-serif text-xl text-white">Privacy & Your Data Rights</h2>
      </div>

      {/* Notice - Article 13 */}
      <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded mb-5 space-y-2">
        <div className="flex items-center gap-2">
          <ShieldCheck size={14} className="text-emerald-400" />
          <p className="text-xs text-white font-medium">What we collect &amp; why</p>
        </div>
        <p className="text-[11px] text-muted-foreground leading-relaxed">
          Assagart collects: <span className="text-white">your contact details, identity documents, asset records, photos and valuations</span>. We process this data to (a) provide the wealth-management service you signed up for (Article 6(1)(b) - performance of contract), and (b) meet our anti-money-laundering and tax-record obligations (Article 6(1)(c) - legal obligation). We never sell your data. Sensitive identity fields are encrypted at rest with AES-256. Activity logs are retained for 6 years to comply with UK Money Laundering Regulations.
        </p>
        <a href="/privacy" className="text-[11px] text-gold hover:underline">Read the full Privacy Policy →</a>
      </div>

      {/* Article 15 - Right of access */}
      <div className="flex items-start justify-between gap-4 p-4 bg-[#0F0F0F] border border-[#1A1A1A] rounded mb-3">
        <div className="flex-1">
          <p className="text-sm text-white font-medium">Download all your data</p>
          <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
            Export a JSON snapshot of every piece of personal data we hold for you, including encrypted profile fields (decrypted in the export), assets, messages and activity log. GDPR Article 15.
          </p>
        </div>
        <Button
          onClick={handleExport}
          disabled={exporting}
          variant="outline"
          className="border-gold/30 text-gold hover:bg-gold/10 flex items-center gap-2 flex-shrink-0"
          data-testid="export-data-btn"
        >
          {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
          Export
        </Button>
      </div>

      {/* Article 17 - Right to erasure */}
      <div className="flex items-start justify-between gap-4 p-4 bg-red-500/5 border border-red-500/20 rounded">
        <div className="flex-1">
          <p className="text-sm text-white font-medium">Delete my account</p>
          <p className="text-[11px] text-muted-foreground mt-1 leading-relaxed">
            Permanently erase your account and all personal data. Activity-log entries are anonymised (no PII retained) but kept for 6 years where legally required for fraud prevention. This action cannot be undone.
          </p>
        </div>
        <Button
          onClick={() => setDeleteOpen(true)}
          variant="outline"
          className="border-red-500/30 text-red-400 hover:bg-red-500/10 flex items-center gap-2 flex-shrink-0"
          data-testid="delete-account-btn"
        >
          <Trash2 size={14} />
          Delete
        </Button>
      </div>

      {/* Confirmation modal */}
      <Dialog open={deleteOpen} onOpenChange={(v) => { setDeleteOpen(v); if (!v) setConfirmText(''); }}>
        <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-md" data-testid="delete-confirm-modal">
          <DialogHeader><DialogTitle className="font-serif text-white flex items-center gap-2"><AlertTriangle size={18} className="text-red-400" /> Permanently delete account</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground leading-relaxed">
              This will permanently delete your account, all assets, all messages, all alerts, and all profile data. <span className="text-white">This cannot be undone.</span>
            </p>
            <p className="text-xs text-muted-foreground italic">
              Type <span className="text-red-400 font-mono">DELETE MY ACCOUNT</span> below to confirm.
            </p>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE MY ACCOUNT"
              className="luxury-input"
              data-testid="delete-confirm-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1 border-white/10 text-muted-foreground" onClick={() => setDeleteOpen(false)}>Cancel</Button>
              <Button
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                disabled={confirmText !== 'DELETE MY ACCOUNT' || deleting}
                onClick={handleDelete}
                data-testid="confirm-delete-btn"
              >
                {deleting ? <Loader2 size={14} className="animate-spin mr-2" /> : <Trash2 size={14} className="mr-2" />}
                Delete forever
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
